import { F0Box } from '@factorialco/f0-react'
import { IndicatorsList, Widget } from '@factorialco/f0-react/dist/experimental'
import { CheckCircle, AlertCircle } from '@factorialco/f0-react/icons/app'
import i18n from 'modules/core/lib/i18n'
import type { IncentivesCompensationPlanPeriodFragment } from 'generated/resources/graphql'
import moment from 'moment'
import { diffBetweenDates, formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import { PERIOD_STATUS, getPeriodStatus } from 'modules/incentives/lib/period'
import { useNavigation } from 'modules/core/hooks/useNavigation'

interface PlanDetailWidgetsProps {
  period: IncentivesCompensationPlanPeriodFragment
}

function getCurrentWidgetData(
  period: IncentivesCompensationPlanPeriodFragment,
  compensationPath: string
): {
  widget: React.ComponentProps<typeof Widget>
  indicator: React.ComponentProps<typeof IndicatorsList>['items'][number]
} {
  const status = getPeriodStatus(period)
  const lastExecution = period.lastExecution
  const completedExecution = lastExecution?.generationStatus == 'completed'
  const daysRemaining = diffBetweenDates(period.endsOn, moment().format('YYYY-MM-DD'))
  const daysAfterDeadline = diffBetweenDates(moment().format('YYYY-MM-DD'), period.endsOn)
  const daysLabel = `${daysRemaining} ${i18n.tp('common.day', { count: daysRemaining })}`
  const deadline = i18n.t('incentives.detail.period_widget.deadline', {
    date: formatBackendDateToDayMonthYear(period.endsOn),
  })

  if (status === PERIOD_STATUS.ACTIVE || status === PERIOD_STATUS.UPCOMING) {
    return {
      widget: {
        header: {
          title: i18n.t('incentives.detail.period_widget.title'),
        },
      },
      indicator: {
        content: daysLabel,
        label: deadline,
      },
    }
  }

  // SENT
  if (completedExecution) {
    return {
      widget: {
        header: {
          title: i18n.t('incentives.detail.sent_widget.title'),
          link: {
            title: i18n.t('sidebar.compensation'),
            url: compensationPath,
          },
        },
      },
      indicator: {
        icon: CheckCircle,
        color: 'text-f1-icon-positive',
        content: moment(lastExecution.executedAt).format('MMMM YYYY'),
        label: i18n.t('incentives.detail.sent_widget.label', {
          date: moment(lastExecution.executedAt).format('DD MMMM YYYY'),
        }),
      },
    }
  }

  // CLOSED
  return {
    widget: {
      header: {
        title: i18n.t('incentives.detail.period_widget.closed.widget_title'),
      },
    },
    indicator: {
      icon: AlertCircle,
      color: 'text-f1-icon-critical',
      content: formatBackendDateToDayMonthYear(period.endsOn),
      label: i18n.tp('incentives.detail.period_widget.closed.indicator_label', {
        count: daysAfterDeadline,
      }),
    },
  }
}

export function CompensationPlanDetailWidgets({ period }: PlanDetailWidgetsProps) {
  const { navigation } = useNavigation()

  const widget = getCurrentWidgetData(period, navigation.compensations.fullPath())

  return (
    <F0Box display="grid" columns="12" gap="sm" paddingLeft="xl">
      <F0Box colSpan="12" md={{ colSpan: '6' }} lg={{ colSpan: '4' }}>
        <Widget fullHeight {...widget.widget}>
          <IndicatorsList items={[widget.indicator]} />
        </Widget>
      </F0Box>
    </F0Box>
  )
}
